import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { I18nProvider } from "./lib/i18n";
import { ThemeProvider } from "./lib/theme";
import { AuthProvider, useAuth } from "./shell/AuthContext";
import Shell from "./shell/Shell";
import { RequireOnline } from "./shell/SyncPill";
import Placeholder from "./shell/Placeholder";

const Login = lazy(() => import("./features/identity/Login"));
const Register = lazy(() => import("./features/identity/Register"));
const SettingsPage = lazy(() => import("./features/identity/SettingsPage"));
const MenuStock = lazy(() => import("./features/catalog/MenuStock"));
const OrdersFeed = lazy(() => import("./features/venue/OrdersFeed"));
const ClientsPage = lazy(() => import("./features/venue/ClientsPage"));
const QrPrintSheet = lazy(() => import("./features/venue/QrPrintSheet"));
const ConsumerPage = lazy(() => import("./features/venue/ConsumerPage"));
const SignAgreement = lazy(() => import("./features/credit/SignAgreement"));
const MessagesPage = lazy(() => import("./features/notify/MessagesPage"));
const SyncIssuesPage = lazy(() => import("./features/sync/SyncIssuesPage"));
const ReportsPage = lazy(() => import("./features/reporting/ReportsPage"));
const BarDashboard = lazy(() => import("./features/reporting/BarDashboard"));
const DistDashboard = lazy(() => import("./features/reporting/DistDashboard"));
const DistributorsPage = lazy(() => import("./features/supply/DistributorsPage"));
const PartnerBarsPage = lazy(() => import("./features/supply/PartnerBarsPage"));
const DistCatalogPage = lazy(() => import("./features/supply/DistCatalogPage"));
const SupplyOrdersPage = lazy(() => import("./features/supply/SupplyOrdersPage"));
const SupplyOrderDetailPage = lazy(() => import("./features/supply/OrderDetailPage"));
const DeliveriesPage = lazy(() => import("./features/supply/DeliveriesPage"));
const RidersPage = lazy(() => import("./features/supply/RidersPage"));
const BarInvoicesPage = lazy(() => import("./features/payments/BarInvoicesPage"));
const DistInvoicesPage = lazy(() => import("./features/payments/DistInvoicesPage"));
const StorefrontPage = lazy(() => import("./features/homedelivery/StorefrontPage"));
const HdStatusPage = lazy(() => import("./features/homedelivery/HdStatusPage"));
const DriverPortal = lazy(() => import("./features/supply/DriverPortal"));
const HdPipelinePage = lazy(() => import("./features/homedelivery/HdPipelinePage"));
const PublicBarPage = lazy(() => import("./features/venue/PublicBarPage"));
const BarsDirectory = lazy(() => import("./features/reservations/BarsDirectory"));
const ManageReservationPage = lazy(() => import("./features/reservations/ManageReservationPage"));
const ReservationsPage = lazy(() => import("./features/reservations/ReservationsPage"));
const SubscriptionPage = lazy(() => import("./features/billing/SubscriptionPage"));
const AdminOverview = lazy(() => import("./features/platform/AdminOverview"));
const AdminOrgs = lazy(() => import("./features/platform/AdminOrgs"));
const AdminOrgDetail = lazy(() => import("./features/platform/AdminOrgDetail"));
const AdminAccounts = lazy(() => import("./features/platform/AdminAccounts"));
const AdminSubscriptions = lazy(() => import("./features/platform/AdminSubscriptions"));
const AdminRevenue = lazy(() => import("./features/platform/AdminRevenue"));
const AdminAnnouncements = lazy(() => import("./features/platform/AdminAnnouncements"));
const AdminAudit = lazy(() => import("./features/platform/AdminAudit"));
const AdminSettings = lazy(() => import("./features/platform/AdminSettings"));

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-inset">
      <div className="text-brand-ink font-heading text-xl animate-pulse">BarPadi…</div>
    </div>
  );
}

function RootRedirect() {
  const { loading, account, workspaces, lastOrgId } = useAuth();
  if (loading) return <Loading />;
  if (!account) return <Navigate to="/login" replace />;
  const last = workspaces.find((w) => w.org_id === lastOrgId) || workspaces[0];
  if (!last) return <Navigate to="/login" replace />;
  if (last.org_type === "platform") return <Navigate to="/admin/overview" replace />;
  return <Navigate to={last.org_type === "distributor" ? "/dist/dashboard" : "/app/dashboard"} replace />;
}

function Guard({ type, children }) {
  const { loading, account, workspaces } = useAuth();
  if (loading) return <Loading />;
  if (!account) return <Navigate to="/login" replace />;
  const has = workspaces.some((w) => w.org_type === type);
  if (!has) {
    const other = workspaces[0];
    if (other) return <Navigate to={other.org_type === "distributor" ? "/dist/dashboard" : other.org_type === "platform" ? "/admin/overview" : "/app/dashboard"} replace />;
    return <Navigate to="/login" replace />;
  }
  return children;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <ThemeProvider>
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<RootRedirect />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/t/:token" element={<ConsumerPage />} />
              <Route path="/sign/:token" element={<SignAgreement />} />
              <Route path="/b/:slug" element={<StorefrontPage />} />
              <Route path="/bar/:slug" element={<PublicBarPage />} />
              <Route path="/bars" element={<BarsDirectory />} />
              <Route path="/bars/:city" element={<BarsDirectory />} />
              <Route path="/r/:token" element={<ManageReservationPage />} />
              <Route path="/hd/:token" element={<HdStatusPage />} />
              <Route path="/d/:token" element={<DriverPortal />} />
              <Route path="/app" element={<Guard type="bar"><Shell kind="bar" /></Guard>}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<BarDashboard />} />
                <Route path="orders" element={<OrdersFeed />} />
                <Route path="reservations" element={<ReservationsPage />} />
                <Route path="delivery" element={<HdPipelinePage />} />
                <Route path="clients" element={<ClientsPage />} />
                <Route path="menu" element={<MenuStock />} />
                <Route path="sync-issues" element={<SyncIssuesPage />} />
                <Route path="distributors" element={<RequireOnline><DistributorsPage /></RequireOnline>} />
                <Route path="distributors/orders/:orderId" element={<RequireOnline><SupplyOrderDetailPage side="bar" /></RequireOnline>} />
                <Route path="invoices" element={<RequireOnline><BarInvoicesPage /></RequireOnline>} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="messages" element={<RequireOnline><MessagesPage /></RequireOnline>} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="settings/subscription" element={<RequireOnline><SubscriptionPage /></RequireOnline>} />
              </Route>
              <Route path="/app/print-qr" element={<Guard type="bar"><QrPrintSheet /></Guard>} />
              <Route path="/dist" element={<Guard type="distributor"><Shell kind="distributor" /></Guard>}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<DistDashboard />} />
                <Route path="orders" element={<SupplyOrdersPage />} />
                <Route path="orders/:orderId" element={<SupplyOrderDetailPage side="distributor" />} />
                <Route path="deliveries" element={<DeliveriesPage />} />
                <Route path="invoices" element={<DistInvoicesPage />} />
                <Route path="bars" element={<PartnerBarsPage />} />
                <Route path="catalog" element={<DistCatalogPage />} />
                <Route path="messages" element={<MessagesPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="settings/riders" element={<RidersPage />} />
              </Route>
              <Route path="/admin" element={<Guard type="platform"><Shell kind="platform" /></Guard>}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<AdminOverview />} />
                <Route path="bars" element={<AdminOrgs type="bar" />} />
                <Route path="distributors" element={<AdminOrgs type="distributor" />} />
                <Route path="orgs/:orgId" element={<AdminOrgDetail />} />
                <Route path="accounts" element={<AdminAccounts />} />
                <Route path="subscriptions" element={<AdminSubscriptions />} />
                <Route path="revenue" element={<AdminRevenue />} />
                <Route path="announcements" element={<AdminAnnouncements />} />
                <Route path="audit" element={<AdminAudit />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          </ThemeProvider>
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
